// Cache Manager for User Data and Profile
class CacheManager {
  constructor() {
    this.storagePrefix = 'ewert_cache_';
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.cacheKeys = {
      USER_PROFILE: 'user_profile',
      USER_CARDS: 'user_cards',
      DASHBOARD_DATA: 'dashboard_data',
      USER_SETTINGS: 'user_settings',
      USER_SUBSCRIPTION: 'user_subscription',
      USER_PAYMENT_METHODS: 'user_payment_methods'
    };
  }

  // Store data with TTL (Time To Live)
  set(key, data, ttl = this.defaultTTL) {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        ttl,
        expires: Date.now() + ttl
      };
      
      const storageKey = this.storagePrefix + key;
      localStorage.setItem(storageKey, JSON.stringify(cacheItem));
      console.log(`[CACHE] Stored ${key} with TTL ${ttl}ms`);
      return true;
    } catch (error) {
      console.error(`[CACHE] Failed to store ${key}:`, error);
      return false;
    }
  }

  // Get data from cache
  get(key) {
    try {
      const storageKey = this.storagePrefix + key;
      const cached = localStorage.getItem(storageKey);
      
      if (!cached) {
        console.log(`[CACHE] No cached data found for ${key}`);
        return null;
      }

      const cacheItem = JSON.parse(cached);
      
      // Check if cache has expired
      if (Date.now() > cacheItem.expires) {
        console.log(`[CACHE] Cache expired for ${key}`);
        this.remove(key);
        return null;
      }

      console.log(`[CACHE] Retrieved ${key} from cache`);
      return cacheItem.data;
    } catch (error) {
      console.error(`[CACHE] Failed to retrieve ${key}:`, error);
      return null;
    }
  }

  // Check if data exists and is valid
  has(key) {
    try {
      const storageKey = this.storagePrefix + key;
      const cached = localStorage.getItem(storageKey);
      
      if (!cached) return false;

      const cacheItem = JSON.parse(cached);
      return Date.now() <= cacheItem.expires;
    } catch (error) {
      console.error(`[CACHE] Failed to check ${key}:`, error);
      return false;
    }
  }

  // Remove specific cache entry
  remove(key) {
    try {
      const storageKey = this.storagePrefix + key;
      localStorage.removeItem(storageKey);
      console.log(`[CACHE] Removed ${key}`);
      return true;
    } catch (error) {
      console.error(`[CACHE] Failed to remove ${key}:`, error);
      return false;
    }
  }

  // Clear all cache
  clear() {
    try {
      const keys = Object.keys(localStorage);
      let cleared = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.storagePrefix)) {
          localStorage.removeItem(key);
          cleared++;
        }
      });
      
      console.log(`[CACHE] Cleared ${cleared} cache entries`);
      return cleared;
    } catch (error) {
      console.error('[CACHE] Failed to clear cache:', error);
      return 0;
    }
  }

  // Clean expired entries
  cleanup() {
    try {
      const keys = Object.keys(localStorage);
      let cleaned = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.storagePrefix)) {
          try {
            const cached = localStorage.getItem(key);
            const cacheItem = JSON.parse(cached);
            
            if (Date.now() > cacheItem.expires) {
              localStorage.removeItem(key);
              cleaned++;
            }
          } catch (e) {
            // Invalid cache entry, remove it
            localStorage.removeItem(key);
            cleaned++;
          }
        }
      });
      
      if (cleaned > 0) {
        console.log(`[CACHE] Cleaned up ${cleaned} expired entries`);
      }
      
      return cleaned;
    } catch (error) {
      console.error('[CACHE] Failed to cleanup cache:', error);
      return 0;
    }
  }

  // Get cache statistics
  getStats() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.storagePrefix));
      const stats = {
        totalEntries: cacheKeys.length,
        validEntries: 0,
        expiredEntries: 0,
        totalSize: 0
      };

      cacheKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          stats.totalSize += cached.length;
          
          const cacheItem = JSON.parse(cached);
          if (Date.now() <= cacheItem.expires) {
            stats.validEntries++;
          } else {
            stats.expiredEntries++;
          }
        } catch (e) {
          stats.expiredEntries++;
        }
      });

      return stats;
    } catch (error) {
      console.error('[CACHE] Failed to get stats:', error);
      return null;
    }
  }

  // Invalidate cache when user data changes
  invalidateUserData() {
    console.log('[CACHE] Invalidating user data cache');
    this.remove(this.cacheKeys.USER_PROFILE);
    this.remove(this.cacheKeys.DASHBOARD_DATA);
    this.remove(this.cacheKeys.USER_CARDS);
    this.remove(this.cacheKeys.USER_SUBSCRIPTION);
    this.remove(this.cacheKeys.USER_PAYMENT_METHODS);
  }

  // Set longer TTL for profile data (1 hour)
  setUserProfile(profileData) {
    return this.set(this.cacheKeys.USER_PROFILE, profileData, 60 * 60 * 1000);
  }

  getUserProfile() {
    return this.get(this.cacheKeys.USER_PROFILE);
  }

  // Set medium TTL for dashboard data (10 minutes)
  setDashboardData(dashboardData) {
    return this.set(this.cacheKeys.DASHBOARD_DATA, dashboardData, 10 * 60 * 1000);
  }

  getDashboardData() {
    return this.get(this.cacheKeys.DASHBOARD_DATA);
  }

  // Set medium TTL for user cards (5 minutes)
  setUserCards(cardsData) {
    return this.set(this.cacheKeys.USER_CARDS, cardsData, 5 * 60 * 1000);
  }

  getUserCards() {
    return this.get(this.cacheKeys.USER_CARDS);
  }

  // Set long TTL for settings (2 hours)
  setUserSettings(settingsData) {
    return this.set(this.cacheKeys.USER_SETTINGS, settingsData, 2 * 60 * 60 * 1000);
  }

  getUserSettings() {
    return this.get(this.cacheKeys.USER_SETTINGS);
  }

  // Set medium TTL for subscription data (10 minutes)
  setUserSubscription(subscriptionData) {
    return this.set(this.cacheKeys.USER_SUBSCRIPTION, subscriptionData, 10 * 60 * 1000);
  }

  getUserSubscription() {
    return this.get(this.cacheKeys.USER_SUBSCRIPTION);
  }

  // Set medium TTL for payment methods (10 minutes)
  setUserPaymentMethods(paymentMethodsData) {
    return this.set(this.cacheKeys.USER_PAYMENT_METHODS, paymentMethodsData, 10 * 60 * 1000);
  }

  getUserPaymentMethods() {
    return this.get(this.cacheKeys.USER_PAYMENT_METHODS);
  }

  // Force refresh - invalidate and reload
  async forceRefresh(api, refreshType = 'all') {
    console.log(`[CACHE] Force refresh requested: ${refreshType}`);
    
    switch (refreshType) {
      case 'profile':
        this.remove(this.cacheKeys.USER_PROFILE);
        break;
      case 'dashboard':
        this.remove(this.cacheKeys.DASHBOARD_DATA);
        break;
      case 'cards':
        this.remove(this.cacheKeys.USER_CARDS);
        break;
      case 'subscription':
        this.remove(this.cacheKeys.USER_SUBSCRIPTION);
        this.remove(this.cacheKeys.USER_PAYMENT_METHODS);
        break;
      default:
        this.invalidateUserData();
    }
    
    return true;
  }
}

export default CacheManager;
